import React, {forwardRef, useImperativeHandle, useRef, useState, useLayoutEffect} from 'react';
import {User} from '../../../types/users';
import Temp from '../../../assets/profile.svg';
import {useOnClickOutside} from '../../../hooks/useOutsideClick';
import {ColleagueInfoPopup, ColleagueInfoPopupHandle} from '../../ColleagueInfoPopup';

type Props = {};

export type ColleaguePopupAvatarListHandle = {
    show: (x: number, y: number, users: Array<User>) => void;
};

export const ColleaguePopupAvatarList = forwardRef<ColleaguePopupAvatarListHandle, Props>(({}: Props, ref) => {
    const [popupVisible, setPopupVisible] = useState(false);
    const [colleagues, setColleagues] = useState<Array<User>>([]);
    const [pos, setPos] = useState({left: 0, top: 0});
    const anchorPosRef = useRef({x: 0, y: 0});
    const popupElRef = useRef<HTMLDivElement | null>(null);
    const colleaguePopupRef = useRef<ColleagueInfoPopupHandle>(null);

    useImperativeHandle(ref, () => ({
        show(x: number, y: number, users: Array<User>) {
            anchorPosRef.current = {x, y};
            setColleagues(users);
            setPopupVisible(true);
        },
    }));

    const closePopup = () => setPopupVisible(false);

    const wrapperRef = useRef<HTMLDivElement>(null);
    useOnClickOutside(wrapperRef, closePopup);

    useLayoutEffect(() => {
        if (!popupVisible) return;

        const margin = 8;
        const vw = window.innerWidth;
        const vh = window.innerHeight;

        setPos({left: anchorPosRef.current.x, top: anchorPosRef.current.y});

        const id = requestAnimationFrame(() => {
            const el = popupElRef.current;
            if (!el) return;
            const rect = el.getBoundingClientRect();

            let left = anchorPosRef.current.x;
            let top = anchorPosRef.current.y;

            if (left + rect.width + margin > vw) {
                left = Math.max(margin, anchorPosRef.current.x - rect.width);
            }
            if (left < margin) left = margin;
            if (top + rect.height + margin > vh) {
                top = Math.max(margin, anchorPosRef.current.y - rect.height);
            }
            if (top < margin) top = margin;

            setPos({left, top});
        });

        return () => cancelAnimationFrame(id);
    }, [popupVisible]);

    return (
        <div ref={wrapperRef}>
            <ColleagueInfoPopup ref={colleaguePopupRef}/>

            {popupVisible && (
                <div
                    ref={popupElRef}
                    className="fixed z-50 bg-white rounded-lg shadow-below p-4 w-[361px] overflow-hidden flex flex-col"
                    style={{left: pos.left, top: pos.top}}
                >
                    <div className="text-[18px]">Пассажиры</div>
                    <div className="flex flex-col gap-5 mt-5">
                        {colleagues?.map((participant) => (
                            <div
                                key={participant.pk}
                                className="flex cursor-pointer"
                                onClick={(e) => colleaguePopupRef.current?.openAtElement(e.currentTarget, participant)}
                            >
                                <img
                                    className="rounded-full flex-[0_0_auto] mr-3 h-[44px] w-[44px] object-cover"
                                    src={participant.photo_img?.file ?? Temp}
                                    alt="profile"
                                />
                                <div
                                    className="flex flex-col text-base overflow-hidden text-[12px] leading-[14px] py-[2px]">
                                    <div className="grow">{participant.first_name} {participant.last_name}</div>
                                    <div className="text-melon-grey-darker">{participant.post}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
});
